import { Injectable, NgZone, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { ToastController } from '@ionic/angular/standalone';
import { filter } from 'rxjs/operators';

/**
 * Periodically checks for new app versions on the server and offers
 * the user a "Reload" toast when one is detected.
 *
 * Why this is needed:
 *   By default, the Angular service worker only checks for updates
 *   when the page (re-)loads, plus on its own background polling
 *   schedule. For a PWA installed to the home screen — where the
 *   user might keep the app open for days — that means new code
 *   pushed to the server doesn't reach them until they manually
 *   quit and reopen, or the SW happens to poll. This service makes
 *   updates discoverable in two extra ways:
 *
 *     1. Periodic explicit check while the app is in the foreground.
 *     2. Re-check on visibility change (returning from another app
 *        or tab is the most common moment we want freshness).
 *
 *   When a version is found, we surface a non-blocking toast with a
 *   "Reload" button. We deliberately don't auto-reload — the user
 *   might be mid-edit on Add Catch, and a silent reload would lose
 *   their work. They control timing.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly toast = inject(ToastController);
  private readonly zone = inject(NgZone);

  /** Poll interval while app is in foreground (15 min). */
  private readonly POLL_INTERVAL_MS = 15 * 60 * 1000;

  private pollHandle: ReturnType<typeof setInterval> | null = null;
  private toastShown = false;

  /**
   * Start watching for updates. Called once on app bootstrap from
   * AppComponent. No-ops in dev mode (where SwUpdate.isEnabled is
   * false) so we don't spam console with "ServiceWorker not enabled".
   */
  init(): void {
    if (!this.swUpdate.isEnabled) return;

    // Subscribe to version-ready events. The SW emits one of these
    // whenever it has finished downloading a new version's assets
    // and they're ready to activate. We surface this as a toast.
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
      )
      .subscribe(() => this.showUpdateToast());

    // Periodic foreground check. setInterval runs outside the zone
    // by default in some configurations, but checkForUpdate() returns
    // a Promise so the zone tracks the work either way.
    this.pollHandle = setInterval(
      () => this.checkSilently(),
      this.POLL_INTERVAL_MS,
    );

    // Visibility-change check: when the user returns to the app
    // after being away, pick up updates that may have been pushed
    // in the meantime. Cheaper than aggressive polling and matches
    // the natural "I just came back, let me see what's new" moment.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkSilently();
      }
    });

    // Also kick off an initial check shortly after bootstrap. Don't
    // do this immediately — let the app finish its initial render
    // and reference-data warmup first.
    setTimeout(() => this.checkSilently(), 5000);
  }

  /**
   * Force an immediate check. Wired from the More page so users have
   * a manual way to pull the latest version. Errors swallowed —
   * a failed check is silent (the user just doesn't get a toast).
   */
  async checkNow(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) return false;
    try {
      return await this.swUpdate.checkForUpdate();
    } catch (err) {
      console.warn('[AppUpdate] checkForUpdate failed', err);
      return false;
    }
  }

  private async checkSilently(): Promise<void> {
    try {
      await this.swUpdate.checkForUpdate();
      // We don't act on the return value here; if a new version
      // was found, the versionUpdates subscription above fires
      // VERSION_READY once the download completes, and the toast
      // is shown from there.
    } catch (err) {
      console.warn('[AppUpdate] checkForUpdate failed', err);
    }
  }

  private async showUpdateToast(): Promise<void> {
    // De-dupe: if multiple version-ready events fire (rare but
    // possible if the SW races itself), don't pile up toasts.
    if (this.toastShown) return;
    this.toastShown = true;

    const t = await this.toast.create({
      message: 'A new version of Saltwater Up is available.',
      position: 'top',
      // No duration — sticks until the user picks an action.
      buttons: [
        {
          text: 'Reload',
          role: 'confirm',
          handler: () => {
            // activateUpdate swaps the new SW into control of the
            // page; reload then loads everything fresh from the
            // new version. We do these inside zone.run so the
            // navigation happens inside Angular's awareness.
            this.zone.run(async () => {
              try {
                await this.swUpdate.activateUpdate();
              } catch (err) {
                console.warn('[AppUpdate] activateUpdate failed', err);
              }
              window.location.reload();
            });
          },
        },
        {
          text: 'Later',
          role: 'cancel',
          handler: () => {
            // Allow the toast to be re-shown if a subsequent check
            // detects yet another version (multi-deploy day).
            this.toastShown = false;
          },
        },
      ],
    });
    await t.present();
  }
}
